import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import BuildingIcon from "@/assets/icons/building.svg?react";
import ChartIcon from "@/assets/icons/chart.svg?react";
import CrossIcon from "@/assets/icons/cross.svg?react";
import ExitIcon from "@/assets/icons/exit.svg?react";
import LogoIcon from "@/assets/icons/logo.svg?react";
import FullLogoIcon from "@/assets/icons/logo-full.svg?react";
import PersonIcon from "@/assets/icons/person.svg?react";
import LayersIcon from "@/assets/icons/three-layers.svg?react";
import Avatar from "@/components/ui/avatar";
import { Typography } from "@/components/ui/typography";
import useScreens from "@/hooks/useScreens";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/useAuthStore";
import useSidebarStore from "@/store/useSidebarStore";

const sideNavigation = [
  {
    to: "/",
    Icon: LayersIcon,
    title: "Jobs",
  },
  {
    to: "/quotes",
    Icon: Banknote,
    title: "Quotes",
  },
  {
    to: "/personal-information",
    Icon: PersonIcon,
    title: "Personal Information",
  },
  {
    to: "/company-information",
    Icon: BuildingIcon,
    title: "Company Information",
  },
  {
    to: "/dashboard",
    Icon: ChartIcon,
    title: "Dashboard",
  },
];

export default function Sidebar() {
  const { isOpen, toggleSidebar, setSidebarState } = useSidebarStore();
  const { setIsAuthorized } = useAuthStore();
  const queryClient = useQueryClient();
  const isMobile = useScreens("md", "down");
  const { data: profile } = useQuery({
    ...profileQueryOptions(),
    select: (response) => ({
      isAdmin: profileSelectors.isAdmin(response),
      isMechanicalOnly: profileSelectors.isMechanicalOnly(response),
      avatar: response.avatar,
      first_name: response.first_name,
      last_name: response.last_name,
      email: response.email,
    }),
  });
  const isAdminRole = profile?.isAdmin;
  const isMechanicalOnly = profile?.isMechanicalOnly;
  const filteredSideNavigation = sideNavigation.filter((nav) => {
    if (!isAdminRole) {
      return nav.to !== "/company-information" && nav.to !== "/dashboard";
    }
    if (isMechanicalOnly && (nav.to === "/dashboard" || nav.to === "/quotes")) {
      return false;
    }
    return true;
  });
  const [animationFinished, setAnimationFinished] = useState(false);

  // Effect to check if sidebar animation is finished
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setAnimationFinished(true), 300); // 300 is the duration of your sidebar animation
      return () => clearTimeout(timer);
    }
    setAnimationFinished(false);
  }, [isOpen]);

  // Effect to handle responsive sidebar behavior at 1200px breakpoint
  useEffect(() => {
    const handleResize = () => {
      const shouldBeOpen = window.innerWidth >= 1200;
      setSidebarState(shouldBeOpen);
    };

    // Set initial state
    handleResize();

    // Add resize listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, [setSidebarState]);

  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      return;
    }

    const shouldLockScroll = isOpen;
    document.body.style.overflow = shouldLockScroll ? "hidden" : "";
    document.documentElement.style.overflow = shouldLockScroll ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isOpen, isMobile]);

  return (
    <div
      className={cn(
        "fixed z-50 flex h-dvh flex-col border-r border-solid border-r-gray-300 bg-white pb-[calc(env(safe-area-inset-bottom)+72px)] transition-all duration-300 ease-in md:z-10 md:h-screen md:pb-0",
        isOpen ? "md:max-w-initial w-full max-w-screen md:w-[286px]" : "hidden md:flex md:w-[81px]"
      )}
    >
      <div className={cn("mt-8 flex items-center justify-between", isOpen ? "pl-4 pr-4 md:pr-0" : "pl-1")}>
        {isOpen ? <FullLogoIcon className="ml-3" /> : <LogoIcon className="ml-3" />}
        <CrossIcon className="block text-gray-500 md:hidden" onClick={toggleSidebar} />
      </div>
      <nav className="mt-6 flex flex-col">
        {filteredSideNavigation.map(({ to, title, Icon }) => {
          return (
            <NavLink
              onClick={() => {
                isMobile && toggleSidebar();
              }}
              key={to}
              to={to}
              className="h-11 cursor-pointer px-4"
            >
              {({ isActive }) => {
                return (
                  <div
                    className={cn(
                      isActive && "bg-primary-50",
                      "flex items-center gap-2 rounded-[6px] px-3 py-2",
                      !isOpen && "h-11 w-11"
                    )}
                  >
                    <Icon className={cn(isActive ? "stroke-primary-400" : "stroke-gray-500", "h-[24px] w-[24px]")} />
                    <Typography
                      variant="text-md"
                      className={cn(isOpen && animationFinished ? "" : "hidden", isActive && "text-primary-400")}
                    >
                      {title}
                    </Typography>
                  </div>
                );
              }}
            </NavLink>
          );
        })}
      </nav>
      <div className="relative mx-2 mt-auto border-t border-solid border-t-gray-200 py-6 pl-4">
        <div className="mt-[10px] flex gap-3">
          <Avatar size={40} url={profile?.avatar ?? undefined} />
          {isOpen && animationFinished && (
            <>
              <div>
                <Typography
                  weight="semibold"
                  className="max-w-[144px] overflow-hidden text-ellipsis text-gray-700"
                  variant="text-small"
                >
                  {profile?.first_name} {profile?.last_name}
                </Typography>
                <Typography
                  variant="text-small"
                  className="max-w-[260px] overflow-hidden text-ellipsis text-gray-600 md:max-w-[141px]"
                >
                  {profile?.email}
                </Typography>
              </div>
              <button
                type="button"
                aria-label="Sign out"
                className="ml-auto rounded-md p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none md:mr-0 md:ml-4"
                onClick={() => {
                  localStorage.clear();
                  queryClient.removeQueries();
                  setIsAuthorized(false);
                }}
              >
                <ExitIcon className="h-6 w-6" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
